import { Component, Input, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { SubjectProperty } from '../../../models/subject-property.model';
import { EnumsService } from '../../../services/enums.service';
import { ConfigurableFieldHostComponent } from '../../configurable-field-host.component';

/**
 * Renders the URLA 2020 "Type of Refinance" and "Purpose of Refinance" dropdowns
 * for the Property Address Refi step. Extends `ConfigurableFieldHostComponent` so
 * it inherits the locale-aware `getLabel` / `isHidden` / `isRequired` helpers and
 * the standard `fieldsToConfig` Input wiring — matches the same pattern that
 * `borrower-info-mini` uses inside the My Info step.
 *
 * The parent decides whether to render this component (gated on
 * `loanPurpose === 'Refinance'`); each dropdown additionally hides itself when
 * the admin marks the field hidden in the field config.
 */
@Component({
    selector: 'refi-options',
    templateUrl: 'refi-options.component.html',
    standalone: false
})
export class RefiOptionsComponent extends ConfigurableFieldHostComponent {

  @Input()
  subjectProperty!: SubjectProperty;

  @ViewChild('refiPurposeCtrl') refiPurposeCtrl: NgModel | undefined;
  @ViewChild('refinancePrimaryPurposeCtrl') refinancePrimaryPurposeCtrl: NgModel | undefined;

  public readonly refiPurposeField = 'subjectProperty.refiPurpose';
  public readonly refinancePrimaryPurposeField = 'subjectProperty.refinancePrimaryPurpose';

  constructor(private readonly _enumsService: EnumsService) {
    super();
  }

  public get refinancePurposes() {
    return this._enumsService.refinancePurposes;
  }

  public get refinancePrimaryPurposes() {
    return this._enumsService.refinancePrimaryPurposes;
  }

  /**
   * Marks each visible-and-required dropdown touched so the invalid-feedback
   * styling kicks in, and returns false if any required dropdown is empty.
   * The parent step component combines this result with its other validations.
   */
  validate = (): boolean => {
    let valid = true;
    if (!this.isHidden(this.refiPurposeField) && this.isRequired(this.refiPurposeField)) {
      this.refiPurposeCtrl?.control.markAsTouched();
      if (!this.refiPurposeCtrl?.control.valid) {
        valid = false;
      }
    }
    if (!this.isHidden(this.refinancePrimaryPurposeField) && this.isRequired(this.refinancePrimaryPurposeField)) {
      this.refinancePrimaryPurposeCtrl?.control.markAsTouched();
      if (!this.refinancePrimaryPurposeCtrl?.control.valid) {
        valid = false;
      }
    }
    return valid;
  }
}
